import { CardType, type ExifData, type GenerationMetadata } from '../types';
import { logger } from '../utils/secureLogger';
import { parseGenerationMetadata, splitAnalysisResponse, type AnalysisResult } from '../utils/generationMetadata';

export type { AnalysisResult } from '../utils/generationMetadata';

/** generate レスポンス：画像 Base64 と画像ステップの生成メタデータ */
export interface PixelArtResult {
  imageBytes: string;
  /** 画像生成に成功したときだけ image ステージを含む。旧サーバーからは undefined */
  generationMetadata?: GenerationMetadata;
}

// 🛡️ 安全なCloud Functions API エンドポイント (Firebase Functions v2)
const CLOUD_FUNCTION_URL = 'https://pixelartapi-cl7nw2mgvq-an.a.run.app';
const GENERATION_METADATA_VERSION = 1;

// ファイルをBase64に変換
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      const base64Data = dataUrl.split(',')[1];
      resolve(base64Data);
    };
    reader.readAsDataURL(file);
  });
}

export const generateCharacterDescription = async (imageFile: File, exifData?: ExifData | null): Promise<AnalysisResult> => {
  try {
    logger.debug('Converting file to base64...');
    const imageData = await fileToBase64(imageFile);
    logger.debug('File converted, size:', imageData.length);
    
    // 🛡️ 認証トークン取得
    const { auth } = await import('../firebase/config');
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Authentication required');
    }
    const token = await user.getIdToken();
    
    logger.debug('Calling Cloud Function for analysis');
    const response = await fetch(CLOUD_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'analyze',
        generationMetadataVersion: GENERATION_METADATA_VERSION,
        imageData: imageData,
        exifData: exifData ? {
          latitude: exifData.latitude,
          longitude: exifData.longitude,
          createDate: exifData.createDate instanceof Date ? exifData.createDate.toISOString() : exifData.createDate,
          model: exifData.model,
        } : null
      })
    });

    logger.debug('Analysis response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Analysis failed', { status: response.status });
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText.slice(0, 200)}`);
    }

    logger.debug('Parsing response...');
    const result = await response.json();
    logger.debug('Analysis completed successfully');
    // generationMetadata は characterData に混ぜず分離して返す（Firestore の characterData を汚さない）
    return splitAnalysisResponse(result);
  } catch (error) {
    logger.error("Error generating character description:", error);
    if (error instanceof Error) {
      if (error.message.includes('quota')) {
        throw new Error("API quota exceeded. Please try again later.");
      }
      if (error.message.includes('Rate limit exceeded')) {
        throw error; // レート制限エラーをそのまま伝播
      }
      if (error.message.includes('Image too large')) {
        throw new Error("Image file is too large. Please use a smaller image (max 5MB).");
      }
    }
    throw new Error("Failed to generate character description. Please try again.");
  }
};

export const checkDailyLimits = async (): Promise<{
  photoUpload: { allowed: boolean; remaining: number; limit: number; currentCount: number };
}> => {
  try {
    // 🛡️ 認証トークン取得
    const { auth } = await import('../firebase/config');
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Authentication required');
    }
    const token = await user.getIdToken();
    
    logger.debug('Checking daily limits from server');
    const response = await fetch(CLOUD_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'checkLimits'
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to check limits: ${response.status}`);
    }

    const result = await response.json();
    logger.debug('Daily limits retrieved successfully');
    return result;
  } catch (error) {
    logger.error("Error checking daily limits:", error);
    // デフォルト値を返す
    return {
      photoUpload: { allowed: true, remaining: 10, limit: 10, currentCount: 0 }
    };
  }
};

export const generatePixelArt = async (characterDescription: string, cardType: CardType, imageData?: string): Promise<PixelArtResult> => {
  try {
    // 🛡️ 認証トークン取得
    const { auth } = await import('../firebase/config');
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Authentication required');
    }
    const token = await user.getIdToken();
    
    logger.debug('Calling Cloud Function for generation');
    logger.debug('Request data', { 
      action: 'generate', 
      cardType, 
      descriptionLength: characterDescription.length,
      hasImageData: !!imageData
    });
    
    const requestBody: any = {
      action: 'generate',
      generationMetadataVersion: GENERATION_METADATA_VERSION,
      description: characterDescription,
      cardType: cardType
    };
    
    // 画像データがある場合は追加
    if (imageData) {
      requestBody.imageData = imageData;
    }
    
    const response = await fetch(CLOUD_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody)
    });

    logger.debug('Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Cloud Function failed', { status: response.status });
      
      if (response.status === 429) {
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(`Rate limit exceeded: ${errorData.error}. Reset time: ${new Date(errorData.resetTime).toLocaleString()}`);
        } catch {
          throw new Error(`Rate limit exceeded. Status: ${response.status}`);
        }
      }
      
      if (response.status === 404) {
        throw new Error(`Cloud Function not found. Please check deployment status.`);
      }
      
      if (response.status === 500) {
        throw new Error(`Cloud Function internal error. ${errorText.slice(0, 200)}`);
      }
      
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText.slice(0, 200)}`);
    }

    const result = await response.json();

    // 画像データのサイズチェック（最小10KB = 約13,333文字のBase64）
    // 正常なピクセルアート画像は通常50KB以上あるため、10KB未満は異常とみなす
    const MIN_IMAGE_SIZE = 13333; // 10KB相当のBase64文字数
    if (!result.imageBytes || result.imageBytes.length < MIN_IMAGE_SIZE) {
      logger.error('Generated image is too small', {
        size: result.imageBytes?.length || 0,
        sizeKB: Math.round((result.imageBytes?.length || 0) * 0.75 / 1024),
        expected: `>= ${MIN_IMAGE_SIZE} chars (10KB)`
      });
      throw new Error('Generated image appears to be corrupted or incomplete. Please try again.');
    }

    return {
      imageBytes: result.imageBytes,
      generationMetadata: parseGenerationMetadata(result.generationMetadata),
    };
  } catch (error) {
    logger.error("Error generating pixel art:", error);
    if (error instanceof Error) {
      if (error.message.includes('quota')) {
        throw new Error("API quota exceeded. Please try again later.");
      } else if (error.message.includes('safety') || error.message.includes('policy')) {
        throw new Error("Content policy restriction. Please try a different image.");
      } else if (error.message.includes('Rate limit exceeded')) {
        throw error; // レート制限エラーをそのまま伝播
      }
    }
    throw new Error("Failed to generate pixel art. Please try again.");
  }
};

